// FROM: https://github.com/modusign/modusign-client/blob/1f2cc00e317b3cc6ddf42ace319c72036470a423/app/src/components/ParticipantFormMeansField/MeansTypeSelect/index.tsx

import { IconLookup } from '@fortawesome/fontawesome-svg-core';
import { faComment, faEnvelope, faUser } from '@fortawesome/pro-regular-svg-icons';
import { faLockAlt, faLockOpenAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Dropdown,
  DropdownButton,
  DropdownOption,
  DropdownOptionList,
  Text,
} from '@modusign/design-system';
import { SigningMethod } from '@modusign/react-signing/types';
import { useSelector } from 'react-redux';

import noop from '@libs/noop';

import { getHasInPersonSigningPermission } from '@app/store/user/authority/selectors';

interface SigningMethodSelectProps {
  value: SigningMethod;
  onSelect: (signingMethod: SigningMethod) => void;
}

const iconMap: Record<SigningMethod, IconLookup | undefined> = {
  EMAIL: faEnvelope,
  KAKAO: faComment,
  IN_PERSON: faUser,
  SECURE_LINK: undefined,
};

export default function SigningMethodSelect({ value, onSelect }: SigningMethodSelectProps) {
  const hasInPersonContractPermission = useSelector(getHasInPersonSigningPermission);
  const icon = iconMap[value];

  return (
    <Dropdown sx={{ width: '80px' }} onChange={noop}>
      <DropdownButton aria-label="signing method select" sx={{ width: '60px' }}>
        {icon && <FontAwesomeIcon icon={icon} />}
      </DropdownButton>
      <DropdownOptionList sx={{ backgroundColor: 'white', width: '140px' }}>
        <SigningMethodCustomOption type={'EMAIL'} onClick={() => onSelect('EMAIL')}>
          <Text color="iconNeutralSlightlyDark" fontSize="md" mr={2}>
            <FontAwesomeIcon icon={faEnvelope} />
          </Text>
          <Text color="textNeutralSlightlyLight" fontSize="md">
            이메일
          </Text>
        </SigningMethodCustomOption>
        <SigningMethodCustomOption type={'KAKAO'} onClick={() => onSelect('KAKAO')}>
          <Text color="iconNeutralSlightlyDark" fontSize="md" mr={2}>
            <FontAwesomeIcon icon={faComment} />
          </Text>
          <Text color="textNeutralSlightlyLight" fontSize="md">
            카카오톡
          </Text>
        </SigningMethodCustomOption>
        <SigningMethodCustomOption type={'IN_PERSON'} onClick={() => onSelect('IN_PERSON')}>
          <Box
            height="100%"
            sx={{
              '.SigningMethod__inPerson-lock-icon': {
                display: hasInPersonContractPermission ? 'none' : 'block',
              },
              '&:hover': {
                '.SigningMethod__inPerson-icon, .SigningMethod__inPerson-text': {
                  color: 'textNeutralSlightlyLight',
                },
                '.SigningMethod__inPerson-lock-icon--close': {
                  display: 'none',
                },
                '.SigningMethod__inPerson-lock-icon--open': {
                  display: 'block',
                },
              },
            }}
            width="100%"
          >
            <Text
              className="SigningMethod__inPerson-icon"
              color={
                hasInPersonContractPermission ? 'iconNeutralSlightlyDark' : 'textNeutralUltraLight'
              }
              fontSize="md"
              mr={2}
            >
              <FontAwesomeIcon icon={faUser} style={{ marginRight: '4px' }} />
            </Text>
            <Text
              className="SigningMethod__inPerson-text"
              color={
                hasInPersonContractPermission ? 'textNeutralSlightlyLight' : 'textNeutralUltraLight'
              }
              fontSize="md"
            >
              대면서명
            </Text>
            <Box
              className="SigningMethod__inPerson-lock-icon"
              color={hasInPersonContractPermission ? 'iconNeutralBase' : 'iconNeutralSlightlyLight'}
              position="absolute"
              right={hasInPersonContractPermission ? '10px' : '12px'}
              top={hasInPersonContractPermission ? '9px' : '10px'}
            >
              <Text
                className="SigningMethod__inPerson-lock-icon--close"
                color="iconNeutralSlightlyLight"
                fontSize="md"
              >
                <FontAwesomeIcon icon={faLockAlt} />
              </Text>
              <Text
                className="SigningMethod__inPerson-lock-icon--open"
                color="iconNeutralBase"
                display="none"
                fontSize="md"
              >
                <FontAwesomeIcon icon={faLockOpenAlt} />
              </Text>
            </Box>
          </Box>
        </SigningMethodCustomOption>
      </DropdownOptionList>
    </Dropdown>
  );
}

/**
 * 대면서명 권한이 없는 경우 업셀링 모달을 열고 선택 이벤트가 취소되어야하는데,
 * Dropdown의 onSelect를 이용할 경우 의도대로 동작하지 않습니다.
 * DropdownOption children 내부에 button을 삽입하여 이벤트가 의도대로 동작하게
 * 구현하기 위해 SigningMethodCustomOption 컴포넌트를 작성합니다.
 */
function SigningMethodCustomOption({
  children,
  disabled,
  type,
  onClick,
}: {
  children: React.ReactNode;
  disabled?: boolean;
  type: SigningMethod;
  onClick: () => void;
}) {
  return (
    <DropdownOption role="button" sx={{ position: 'relative' }} value={type}>
      <Box
        as="button"
        disabled={disabled}
        height="100%"
        left={0}
        padding="8px 12px"
        position="absolute"
        sx={{
          '&:disabled': {
            color: 'textNeutralUltraLight',
          },
          '&:disabled:hover': {
            color: 'textNeutralSlightlyLight',
          },
        }}
        textAlign="left"
        top={0}
        width="100%"
        onClick={onClick}
      >
        {children}
      </Box>
    </DropdownOption>
  );
}
